/**
 * SE-6488: the board-level Sort vocabulary used by the header toolbar row's
 * `Sort` control (Figma node 80:15019).
 *
 * Deliberately the SAME option vocabulary the per-column sort menu already
 * uses (`KanbanColumnSortMenuContainer`'s `SortOption`), so the board control
 * and the column control never disagree about what "GPR" or "Contract" means.
 * The board sort pre-orders each column's players; a column left on its default
 * `rank` sort renders that incoming order untouched, so the board sort shows
 * through without either control fighting the other.
 * @module features/player-kanban/constants/boardSort
 */

/** A board-level sort field. `none` is the unsorted default (server order). */
export type BoardSortOption = "none" | "name" | "age" | "gpr" | "contract";

/** Sort direction for the board-level sort. */
export type BoardSortDirection = "asc" | "desc";

/** The board's sort selection. */
export interface BoardSortState {
  readonly option: BoardSortOption;
  readonly direction: BoardSortDirection;
}

/** No board-level sort applied — columns render in server order. */
export const DEFAULT_BOARD_SORT: BoardSortState = {
  option: "none",
  direction: "asc",
};

/** Menu labels, in display order. `none` first so it reads as the reset. */
export const BOARD_SORT_OPTIONS: readonly {
  readonly key: BoardSortOption;
  readonly label: string;
}[] = [
  { key: "none", label: "Default order" },
  { key: "name", label: "Name" },
  { key: "age", label: "Age" },
  { key: "gpr", label: "GPR" },
  { key: "contract", label: "Contract value" },
];

/**
 * Direction labels for the menu's second group, in display order.
 *
 * SE-6554: the board sort menu adopts the global player search's structure —
 * the sortable fields, then a divider, then the direction. Direction was always
 * part of `BoardSortState`; until now the only way to reach it was to re-pick
 * the field that was already active, which no affordance advertised.
 */
export const BOARD_SORT_DIRECTIONS: readonly {
  readonly key: BoardSortDirection;
  readonly label: string;
}[] = [
  { key: "asc", label: "Ascending" },
  { key: "desc", label: "Descending" },
];

/**
 * The toolbar button's visible label — bare `Sort` until a sort is chosen, then
 * the FIELD NAME on its own.
 *
 * SE-6554 (Raniel, 2026-08-06): the trigger drops the old `Sort: <label>` prefix
 * and reads just the field, because the direction now rides beside it as an
 * arrow icon rather than as words. This is how the shared search-modal sort
 * trigger already behaves, and the board should not invent a second dialect.
 * @param sort - The current board sort state.
 * @returns The button label.
 */
export const boardSortButtonLabel = (sort: BoardSortState): string => {
  if (sort.option === "none") return "Sort";
  const match = BOARD_SORT_OPTIONS.find(o => o.key === sort.option);
  return match ? match.label : "Sort";
};

/**
 * Next sort state when the user picks `option`.
 *
 * Field and direction are INDEPENDENT axes, because the menu now renders both
 * (SE-6554). Picking a field sets the field and keeps the direction the user
 * already chose; picking `none` clears the sort back to server order.
 *
 * This deliberately drops the previous "re-picking the active field flips its
 * direction" behaviour. That flip was the only way to reach direction while it
 * was unrendered; with explicit `Ascending` / `Descending` rows on screen it
 * becomes actively misleading — a second press on `GPR` would move the
 * direction highlight with nothing having asked for it.
 * @param current - The current sort state.
 * @param option - The field the user just selected.
 * @returns The next sort state.
 */
export const nextBoardSort = (
  current: BoardSortState,
  option: BoardSortOption
): BoardSortState => {
  if (option === "none") return DEFAULT_BOARD_SORT;
  return { option, direction: current.direction };
};

/**
 * Next sort state when the user picks a direction.
 *
 * A no-op while no field is sorted: `none` renders in server order, which has
 * no direction to reverse.
 * @param current - The current sort state.
 * @param direction - The direction the user just selected.
 * @returns The next sort state.
 */
export const withBoardSortDirection = (
  current: BoardSortState,
  direction: BoardSortDirection
): BoardSortState =>
  current.option === "none" ? current : { option: current.option, direction };
